import { useEffect, useRef, useState, useCallback } from "react";
import {
  displayAdManager,
  SlotConfig,
  SlotSize,
  SizeMapping,
} from "MVVM/Utilities/Ads/DisplayAdManager";

interface DisplayAdConfig {
  networkId: string;
  adUnit: string;
}

interface UseDisplayAdOptions {
  slotId: string;
  sizes: SlotSize[];
  sizeMapping?: SizeMapping;
  displayConfig: DisplayAdConfig | null;
  game?: string;
  enabled?: boolean;
  onAdLoaded?: (hasAd: boolean) => void;
  timeout?: number;
}

interface UseDisplayAdResult {
  containerRef: (node: HTMLDivElement | null) => void;
  hasAd: boolean;
  isLoading: boolean;
  isResolved: boolean;
}

export function useDisplayAd({
  slotId,
  sizes,
  sizeMapping,
  displayConfig,
  game,
  enabled = true,
  onAdLoaded,
  timeout = 3000,
}: UseDisplayAdOptions): UseDisplayAdResult {
  const [hasAd, setHasAd] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isResolved, setIsResolved] = useState(false);
  const [domReady, setDomReady] = useState(false);

  const mountedRef = useRef(true);
  const slotCreatedRef = useRef(false);
  const callbackFiredRef = useRef(false);
  const onAdLoadedRef = useRef(onAdLoaded);

  onAdLoadedRef.current = onAdLoaded;

  const fireCallback = useCallback((adLoaded: boolean) => {
    if (callbackFiredRef.current || !mountedRef.current) return;
    callbackFiredRef.current = true;
    setHasAd(adLoaded);
    setIsLoading(false);
    setIsResolved(true);
    onAdLoadedRef.current?.(adLoaded);
  }, []);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setDomReady(true);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    callbackFiredRef.current = false;
    slotCreatedRef.current = false;
    setHasAd(false);
    setIsLoading(true);
    setIsResolved(false);
  }, [slotId]);

  useEffect(() => {
    if (!enabled || !displayConfig) {
      fireCallback(false);
      return;
    }

    if (!domReady) {
      return;
    }

    if (slotCreatedRef.current) {
      return;
    }

    slotCreatedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const testAds = params.get("testAds");
    const viewerSource = window.location.href.includes("iframe")
      ? "iframe"
      : "website";

    const targeting: Record<string, string> = {
      viewer_source: viewerSource,
    };

    if (testAds) {
      targeting.test = testAds;
    }

    if (game) {
      targeting.game = game;
    }

    const config: SlotConfig = {
      adUnitPath: `/${displayConfig.networkId}/${displayConfig.adUnit}`,
      sizes,
      sizeMapping,
      targeting,
    };

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    displayAdManager
      .createSlot(slotId, config, (event) => {
        if (!mountedRef.current) return;
        const adLoaded = !event.isEmpty;
        fireCallback(adLoaded);
      })
      .then((slot) => {
        if (!mountedRef.current) return;

        if (!slot) {
          fireCallback(false);
          return;
        }

        displayAdManager.refreshSlot(slotId);

        timeoutId = setTimeout(() => {
          fireCallback(false);
        }, timeout);
      })
      .catch(() => {
        if (mountedRef.current) {
          fireCallback(false);
        }
      });

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    slotId,
    sizes,
    sizeMapping,
    displayConfig,
    game,
    enabled,
    domReady,
    timeout,
    fireCallback,
  ]);

  useEffect(() => {
    return () => {
      displayAdManager.destroySlot(slotId);
    };
  }, [slotId]);

  return {
    containerRef,
    hasAd,
    isLoading,
    isResolved,
  };
}
